import type { UploadManifest, UploadManifestFile } from "@/lib/storage/upload-manifest-store";
import { getLiveBytesForFile } from "@/lib/upload/upload-live-progress";

export type FileUploadProgress = {
  clientFileKey: string;
  serverFileId: string;
  name: string;
  sizeBytes: number;
  bytesUploaded: number;
  status: UploadManifestFile["status"];
  error?: string;
};

export type SessionUploadProgress = {
  bytesUploaded: number;
  bytesTotal: number;
  filesComplete: number;
  filesTotal: number;
  files: FileUploadProgress[];
};

export type UploadPhase = "idle" | "uploading" | "complete" | "error";

export function partByteSize(
  fileSize: number,
  partSize: number,
  partNumber: number,
  totalParts: number,
) {
  if (partNumber < totalParts) return partSize;
  const consumed = (totalParts - 1) * partSize;
  return fileSize - consumed;
}

export function totalPartsForFile(fileSize: number, partSize: number) {
  return Math.max(1, Math.ceil(fileSize / partSize));
}

function bytesUploadedForFile(file: UploadManifestFile) {
  let bytes = 0;

  if (file.status === "uploaded") {
    bytes = file.sizeBytes;
  } else if (!file.multipart) {
    bytes = file.completedParts.length > 0 ? file.sizeBytes : 0;
  } else {
    const totalParts = totalPartsForFile(file.sizeBytes, file.partSize);
    const completed = new Set(file.completedParts.map((part) => part.partNumber));
    for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
      if (completed.has(partNumber)) {
        bytes += partByteSize(file.sizeBytes, file.partSize, partNumber, totalParts);
      }
    }
  }

  return bytes + getLiveBytesForFile(file.clientFileKey);
}

export function computeSessionUploadProgress(manifest: UploadManifest): SessionUploadProgress {
  const files = manifest.files.map((file) => ({
    clientFileKey: file.clientFileKey,
    serverFileId: file.serverFileId,
    name: file.name,
    sizeBytes: file.sizeBytes,
    bytesUploaded: bytesUploadedForFile(file),
    status: file.status,
    error: file.status === "failed" ? file.error : undefined,
  }));

  const bytesUploaded = files.reduce((sum, file) => sum + file.bytesUploaded, 0);
  const bytesTotal = files.reduce((sum, file) => sum + file.sizeBytes, 0);
  const filesComplete = files.filter((file) => file.status === "uploaded").length;

  return {
    bytesUploaded,
    bytesTotal,
    filesComplete,
    filesTotal: files.length,
    files,
  };
}

export function deriveUploadPhase(progress: SessionUploadProgress): {
  phase: UploadPhase;
  error: string | null;
} {
  if (progress.filesComplete === progress.filesTotal) {
    return { phase: "complete", error: null };
  }

  if (progress.files.some((file) => file.status === "uploading")) {
    return { phase: "uploading", error: null };
  }

  if (progress.files.some((file) => file.status === "failed")) {
    const failed = progress.files.find((file) => file.status === "failed");
    return { phase: "error", error: failed?.error ?? "Some uploads failed" };
  }

  return { phase: "uploading", error: null };
}

export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function formatPercentValue(bytesUploaded: number, bytesTotal: number) {
  if (bytesTotal <= 0) return 0;
  return Math.min(100, (bytesUploaded / bytesTotal) * 100);
}

export function formatPercent(bytesUploaded: number, bytesTotal: number) {
  return Math.round(formatPercentValue(bytesUploaded, bytesTotal));
}

export function formatPercentDisplay(bytesUploaded: number, bytesTotal: number) {
  const value = formatPercentValue(bytesUploaded, bytesTotal);
  if (value >= 100) return "100.00";
  return value.toFixed(2);
}

export function formatByteProgress(bytesUploaded: number, bytesTotal: number) {
  return `${formatBytes(bytesUploaded)} / ${formatBytes(bytesTotal)}`;
}

export function formatFilesProgressLabel(filesComplete: number, filesTotal: number) {
  return `${filesComplete} of ${filesTotal} clips`;
}

/**
 * Tab-title prefix that keeps live upload progress in front of the viewer even
 * when the tab is in the background, e.g. "42% · Session name".
 */
export function formatUploadProgressTitle(percent: number, baseTitle: string) {
  return `${percent}% · ${baseTitle}`;
}

export function formatPercentWithSymbol(bytesUploaded: number, bytesTotal: number) {
  return `${formatPercentDisplay(bytesUploaded, bytesTotal)}%`;
}

export function formatTransferRate(bytesPerSecond: number) {
  if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) return null;
  return `${formatBytes(bytesPerSecond)}/s`;
}

export function formatTimeRemaining(totalSeconds: number) {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return null;

  const seconds = Math.ceil(totalSeconds);
  if (seconds < 60) return `${seconds}s left`;

  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `${minutes}m left`;

  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (remMinutes === 0) return `${hours}h left`;
  return `${hours}h ${remMinutes}m left`;
}

export function estimateTimeRemaining(bytesRemaining: number, bytesPerSecond: number) {
  const seconds = estimateSecondsRemaining(bytesRemaining, bytesPerSecond);
  if (seconds === null) return null;
  return formatTimeRemaining(seconds);
}

export function estimateSecondsRemaining(
  bytesRemaining: number,
  bytesPerSecond: number,
) {
  if (bytesRemaining <= 0) return null;
  if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) return null;
  return bytesRemaining / bytesPerSecond;
}
